import { useEffect, useRef, useState, type ReactElement } from 'react';
import { SPACES, VOCAB, pick, pickWeighted, fmtTs } from '../data/landing-mock';

/* ── tiny helpers ── */
const randInt = (lo: number, hi: number): number =>
  Math.floor(Math.random() * (hi - lo + 1)) + lo;

/* ── Sparkline SVG ── */
function drawSparkSvg(
  data: number[],
  width: number,
  height: number,
  color: string,
): string {
  const n = data.length;
  if (n < 2) return '';
  const min = Math.min(...data);
  const max = Math.max(...data);
  const rng = Math.max(1, max - min);
  const stepX = width / (n - 1);
  const pts = data
    .map((v, i) => {
      const x = i * stepX;
      const y = height - 3 - ((v - min) / rng) * (height - 6);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const areaPts = `0,${height} ${pts} ${width},${height}`;
  const lastX = (n - 1) * stepX;
  const lastY = height - 3 - ((data[n - 1]! - min) / rng) * (height - 6);
  const uid = color.replace(/[^\w]/g, '');
  return `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="g-${uid}" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="${color}" stop-opacity="0.30"/>
          <stop offset="1" stop-color="${color}" stop-opacity="0"/>
        </linearGradient>
      </defs>
      <polygon points="${areaPts}" fill="url(#g-${uid})"/>
      <polyline points="${pts}" fill="none" stroke="${color}" stroke-width="1.5" stroke-linejoin="round"/>
      <circle cx="${lastX.toFixed(1)}" cy="${lastY.toFixed(1)}" r="2.5" fill="${color}"/>
    </svg>`;
}

/* ── Counter card ── */
function CounterCard({
  label,
  value,
  color,
  foot,
  points,
}: {
  label: string;
  value: number;
  color: string;
  foot: ReactElement;
  points: number[];
}): ReactElement {
  const chartRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState('');

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const w = el.clientWidth || 240;
    const h = el.clientHeight || 46;
    setSvg(drawSparkSvg(points, w, h, color));
  }, [points, color]);

  // repaint on resize
  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth || 240;
      const h = el.clientHeight || 46;
      setSvg(drawSparkSvg(points, w, h, color));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [points, color]);

  return (
    <div className="relative overflow-hidden rounded-lg border border-slate-800 bg-panel p-5">
      {/* top glow line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
      <div className="text-[11px] text-slate-500 uppercase tracking-wider font-mono mb-3">
        {label}
      </div>
      <div
        className="text-[44px] font-mono font-medium leading-none tabular-nums mb-5"
        style={{ color }}
      >
        {value.toLocaleString()}
      </div>
      <div ref={chartRef} className="h-[46px] mb-3" dangerouslySetInnerHTML={{ __html: svg }} />
      <div className="text-[11px] text-slate-500 font-mono flex justify-between items-center gap-2">
        {foot}
      </div>
    </div>
  );
}

/* ── Live feed row ── */
interface FeedRow {
  ts: string;
  agent: string;
  fn: string;
  actor: string;
  spaceId: string;
  status: 'succeeded' | 'executing' | 'preempted';
  extra?: string;
}

function makeFeedRow(): FeedRow {
  const sp = pick(SPACES);
  const ag = pick(VOCAB.agents);
  const fn = pickWeighted(VOCAB.functions);
  const ac = pick(VOCAB.actorsBySpace[sp.id] ?? ['BP_Actor']);
  const ts = fmtTs();

  const r = Math.random();
  let status: FeedRow['status'] = 'succeeded';
  if (r > 0.92) status = 'preempted';
  else if (r > 0.75) status = 'executing';

  let extra: string | undefined;
  if (fn === 'move_actor') {
    extra = `, {x:${randInt(-2000, 2000)}, y:${randInt(-2000, 2000)}, z:${randInt(0, 300)}}`;
  } else if (fn === 'set_actor_property') {
    extra = `, "${pick(['Intensity', 'Color', 'MaxWalkSpeed', 'FOV', 'bVisible'])}"`;
  }

  return { ts, agent: ag, fn, actor: ac, spaceId: sp.id, status, extra };
}

const FEED_MAX = 12;

/* ── Main page ── */
export function TelemetryDashboard(): ReactElement {
  const [ts, setTs] = useState(fmtTs());

  // counters
  const ws = useRef({ val: 2314, points: seed(32, 2200, 40) });
  const intents = useRef({ val: 1847, points: seed(32, 1800, 120) });
  const actors = useRef({ val: 12874, points: seed(32, 12500, 200) });
  const [wsVal, setWsVal] = useState(ws.current.val);
  const [intVal, setIntVal] = useState(intents.current.val);
  const [actVal, setActVal] = useState(actors.current.val);
  const [wsPts, setWsPts] = useState([...ws.current.points]);
  const [intPts, setIntPts] = useState([...intents.current.points]);
  const [actPts, setActPts] = useState([...actors.current.points]);

  // feed
  const [feedFilter, setFeedFilter] = useState('all');
  const [feedRows, setFeedRows] = useState<FeedRow[]>(() =>
    Array.from({ length: FEED_MAX }, makeFeedRow),
  );

  // clock
  useEffect(() => {
    const iv = setInterval(() => setTs(fmtTs()), 43);
    return () => clearInterval(iv);
  }, []);

  // dashboard tick
  useEffect(() => {
    const tick = (): void => {
      ws.current.val += randInt(-2, 3);
      ws.current.val = Math.max(2100, ws.current.val);
      intents.current.val += randInt(-40, 90);
      intents.current.val = Math.max(1400, intents.current.val);
      actors.current.val += randInt(-20, 40);
      actors.current.val = Math.max(12000, actors.current.val);

      ws.current.points.push(ws.current.val);
      intents.current.points.push(intents.current.val);
      actors.current.points.push(actors.current.val);
      if (ws.current.points.length > 32) ws.current.points.shift();
      if (intents.current.points.length > 32) intents.current.points.shift();
      if (actors.current.points.length > 32) actors.current.points.shift();

      setWsVal(ws.current.val);
      setIntVal(intents.current.val);
      setActVal(actors.current.val);
      setWsPts([...ws.current.points]);
      setIntPts([...intents.current.points]);
      setActPts([...actors.current.points]);
    };
    const iv = setInterval(tick, 1800);
    return () => clearInterval(iv);
  }, []);

  // feed tick
  useEffect(() => {
    const tick = (): void => {
      setFeedRows((prev) => {
        const next = [makeFeedRow(), ...prev];
        if (next.length > FEED_MAX) next.length = FEED_MAX;
        return next;
      });
    };
    const iv = setInterval(tick, 900);
    return () => clearInterval(iv);
  }, []);

  const filtered =
    feedFilter === 'all'
      ? feedRows
      : feedRows.filter((r) => r.fn === feedFilter);

  const statusClass = (s: FeedRow['status']): string => {
    if (s === 'succeeded') return 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30';
    if (s === 'executing') return 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30';
    return 'text-rose-400 bg-rose-400/10 border-rose-400/30';
  };

  return (
    <section className="space-y-6">
     
      {/* ── Counter cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CounterCard
          label="Active WebSocket Connections"
          value={wsVal}
          color="#e8e8ee"
          foot={<><span className="text-emerald-400">▲ 12%</span> vs last hour</>}
          points={wsPts}
        />
        <CounterCard
          label="Intents fired · last 60s"
          value={intVal}
          color="#ff2e88"
          foot={<>p95 latency <span className="text-slate-300">42ms</span></>}
          points={intPts}
        />
        <CounterCard
          label="Actors online"
          value={actVal}
          color="#00d9ff"
          foot={<span className="text-slate-500">character · camera · light · fx · generic</span>}
          points={actPts}
        />
      </div>

      {/* ── Live feed ── */}
      <div className="rounded-lg border border-slate-800 bg-panel overflow-hidden">
        {/* feed header */}
        <div className="px-5 py-4 border-b border-slate-800 flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-2.5 font-mono text-xs text-slate-300">
            <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(0,255,163,0.5)] animate-pulse" />
            intent-stream · region:us-west
          </div>
          <div className="flex gap-1">
            {(['all', 'move_actor', 'call_actor_function', 'spawn_actor'] as const).map(
              (tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setFeedFilter(tab)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-mono border transition-colors ${
                    feedFilter === tab
                      ? 'text-cyan-400 bg-cyan-400/5 border-cyan-400/25'
                      : 'text-slate-500 border-transparent hover:text-slate-300'
                  }`}
                >
                  {tab === 'all' ? 'all' : tab}
                </button>
              ),
            )}
          </div>
        </div>
        {/* feed body */}
        <div
          className="px-5 py-2 h-[320px] overflow-hidden relative"
          style={{
            maskImage:
              'linear-gradient(180deg, transparent, black 10%, black 90%, transparent)',
          }}
        >
          {filtered.map((row, i) => (
            <div
              key={`${row.ts}-${i}`}
              className="grid gap-4 items-center py-2.5 border-b border-slate-800/50 font-mono text-[12.5px] animate-[feedIn_0.4s_ease-out]"
              style={{
                gridTemplateColumns: '100px 1fr auto auto',
              }}
            >
              <span className="text-slate-600">{row.ts}</span>
              <span className="text-slate-300 truncate">
                <span className="text-rose-400">agent://{row.agent}</span>
                <span className="text-slate-600 mx-1">→</span>
                <span className="text-cyan-400">{row.fn}</span>
                <span className="text-slate-300">
                  ({row.actor}{row.extra ?? ''})
                </span>
              </span>
              <span className="text-emerald-400">{row.spaceId}</span>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusClass(row.status)}`}
              >
                {row.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── seed sparkline history ── */
function seed(count: number, base: number, drift: number): number[] {
  const arr: number[] = [];
  let v = base;
  for (let i = 0; i < count; i++) {
    v += (Math.random() - 0.45) * drift;
    v = Math.max(base * 0.6, v);
    arr.push(Math.round(v));
  }
  return arr;
}
